import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, X, Check, AlertCircle } from 'lucide-react';

import API_BASE_URL from "../config";

const InventoryDestinationManager = () => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDestination, setEditingDestination] = useState(null);
  const [notification, setNotification] = useState(null);

  const [formData, setFormData] = useState({
    destinationId: 0,
    destinationCode: '',
    wms: false,
    asset: false,
    both: false,
    destinationName: '',
    description: '',
    view: false,
    isActive: true

  });

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}api/InventoryDestination`);
      const data = await response.json();
      setDestinations(Array.isArray(data) ? data : []);
    } catch (error) {
      showNotification('Failed to fetch destinations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = { ...formData };

    try {
      const method = editingDestination ? 'PUT' : 'POST';
      const url = editingDestination
        ? `${API_BASE_URL}api/InventoryDestination/${editingDestination.destinationId}`
        : `${API_BASE_URL}api/InventoryDestination`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showNotification(
          editingDestination ? 'Destination updated successfully' : 'Destination created successfully'
        );
        closeModal();
        fetchDestinations();
      } else {
        throw new Error('Operation failed');
      }
    } catch (error) {
      showNotification('Operation failed. Please try again.', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this destination?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}api/InventoryDestination/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showNotification('Destination deleted successfully');
        fetchDestinations();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      showNotification('Failed to delete destination', 'error');
    }
  };

  const openModal = (destination = null) => {
    if (destination) {
      setEditingDestination(destination);
      setFormData(destination);
    } else {
      setEditingDestination(null);
      setFormData({
        destinationId: 0,
        destinationCode: '',
        wms: false,
        asset: false,
        both: false,
        destinationName: '',
        description: '', view: false,
        isActive: true
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingDestination(null);
  };

  const handleDestinationTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      wms: type === 'wms',
      asset: type === 'asset',
      both: type === 'both'
    }));
  };

  const getDestinationType = (dest) => {
    if (dest.wms) return 'WMS';
    if (dest.asset) return 'ASSET';
    if (dest.both) return 'BOTH';
    return 'N/A';
  };

  const getCurrentDestinationType = () => {
    if (formData.wms) return 'wms';
    if (formData.asset) return 'asset';
    if (formData.both) return 'both';
    return '';
  };

  const filteredDestinations = destinations.filter(dest =>
    dest.destinationCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dest.destinationName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dest.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 text-gray-900 p-6 font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Outfit:wght@300;400;500;600;700&display=swap');
        
        * {
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        code, .mono {
          font-family: 'JetBrains Mono', monospace;
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-in {
          animation: slideInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        .animate-in-delayed {
          animation: slideInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards;
          opacity: 0;
        }
        
        .animate-slide-right {
          animation: slideInRight 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        .animate-fade {
          animation: fadeIn 0.3s ease-in-out;
        }
        
        .animate-scale {
          animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .glass {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(59, 130, 246, 0.1);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
        }
        
        .glass-strong {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(59, 130, 246, 0.15);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          color: white;
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
        }
        
        .btn-primary:active {
          transform: translateY(0);
        }
        
        .btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .btn-danger:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(239, 68, 68, 0.3);
        }
        
        .table-row {
          transition: all 0.2s ease;
        }
        
        .table-row:hover {
          background: rgba(59, 130, 246, 0.04);
          transform: translateX(4px);
        }
        
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        
        .badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .badge-wms {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }
        
        .badge-asset {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          color: white;
        }
        
        .badge-both {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
        }
        
        .badge-active {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }
        
        .badge-inactive {
          background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
          color: white;
        }
        
        .modal-backdrop {
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(8px);
        }
        
        .scrollbar-custom::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        .scrollbar-custom::-webkit-scrollbar-track {
          background: rgba(59, 130, 246, 0.05);
          border-radius: 4px;
        }
        
        .scrollbar-custom::-webkit-scrollbar-thumb {
          background: rgba(59, 130, 246, 0.2);
          border-radius: 4px;
        }
        
        .scrollbar-custom::-webkit-scrollbar-thumb:hover {
          background: rgba(59, 130, 246, 0.3);
        }
      `}</style>

      {/* Notification */}
      {notification && (
        <div className={`fixed top-6 right-6 glass-strong rounded-lg px-6 py-4 flex items-center gap-3 z-50 animate-slide-right ${notification.type === 'error' ? 'border-red-500/30' : 'border-blue-500/30'
          }`}>
          {notification.type === 'error' ? (
            <AlertCircle className="w-5 h-5 text-red-500" />
          ) : (
            <Check className="w-5 h-5 text-blue-500" />
          )}
          <span className="text-sm font-medium text-gray-900">{notification.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8 animate-in">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-2 h-12 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
              <h1 className="text-4xl font-bold tracking-tight text-gray-900">
                Inventory <span className="text-blue-600">Destinations</span>
              </h1>
            </div>
            <p className="text-gray-600 text-sm ml-5">Manage WMS and Asset destination configurations</p>
          </div>
          <button
            onClick={() => openModal()}
            className="btn-primary px-6 py-3 rounded-lg font-semibold flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            New Destination
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="max-w-7xl mx-auto mb-6 animate-in-delayed">
        <div className="glass rounded-lg p-1">
          <div className="flex items-center gap-3 px-4 py-2">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code, name, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-transparent border-none text-sm text-gray-900 placeholder-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto animate-in-delayed">
        <div className="glass rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500 text-sm">Loading destinations...</p>
              </div>
            </div>
          ) : filteredDestinations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <AlertCircle className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm">
                {searchTerm ? 'No destinations match your search' : 'No destinations found'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-custom">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Code</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Description</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">View</th>

                    <th className="text-right px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDestinations.map((dest, index) => (
                    <tr key={dest.destinationId} className="border-b border-gray-100 table-row" style={{ animationDelay: `${index * 0.05}s` }}>
                      <td className="px-6 py-4">
                        <code className="mono text-sm font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          {dest.destinationCode}
                        </code>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{dest.destinationName}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`badge ${dest.wms ? 'badge-wms' : dest.asset ? 'badge-asset' : 'badge-both'
                          }`}>
                          {getDestinationType(dest)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">{dest.description || '—'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`badge ${dest.isActive ? 'badge-active' : 'badge-inactive'}`}>
                          {dest.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`badge ${dest.view ? 'badge-active' : 'badge-inactive'}`}>
                          {dest.isActive ? 'View' : 'not alllowed'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openModal(dest)}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors group"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                          </button>
                          <button
                            onClick={() => handleDelete(dest.destinationId)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors group"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop animate-fade">
          <div className="glass-strong rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto scrollbar-custom animate-scale">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingDestination ? 'Edit Destination' : 'New Destination'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-5">
                {/* Destination Code */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Destination Code *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.destinationCode}
                    onChange={(e) => setFormData({ ...formData, destinationCode: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 transition-all"
                    placeholder="e.g., PRPO01"
                  />
                </div>

                {/* Destination Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Destination Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.destinationName}
                    onChange={(e) => setFormData({ ...formData, destinationName: e.target.value })}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 transition-all"
                    placeholder="e.g., PRdv1"
                  />
                </div>
              </div>

              {/* Destination Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Destination Type *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {['wms', 'asset', 'both'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleDestinationTypeChange(type)}
                      className={`px-4 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all ${getCurrentDestinationType() === type
                        ? type === 'wms'
                          ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/30'
                          : type === 'asset'
                            ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-lg shadow-purple-900/30'
                            : 'bg-gradient-to-br from-amber-600 to-amber-700 text-white shadow-lg shadow-amber-900/30'
                        : 'bg-white border border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm text-gray-900 transition-all resize-none"
                  placeholder="Enter description..."
                  rows={3}
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500 focus:ring-offset-white"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Active Status
                </label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="view"
                  checked={formData.view}
                  onChange={(e) => setFormData({ ...formData, view: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500 focus:ring-offset-white"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Active View
                </label>
              </div>
              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg font-semibold text-sm text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary px-4 py-2.5 rounded-lg font-semibold text-sm"
                >
                  {editingDestination ? 'Update' : 'Create'} Destination
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryDestinationManager;